import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import WelcomeScene from '../components/WelcomeScene';

const DOT = { ok:'var(--green)', warn:'var(--amber)', down:'var(--red)' };
const CHIP = {
  ok:        { bg:'rgba(52,211,153,0.08)', bd:'rgba(52,211,153,0.4)', fg:'var(--green)' },
  attention: { bg:'rgba(245,191,36,0.10)', bd:'rgba(245,191,36,0.5)', fg:'var(--amber)' },
  critical:  { bg:'rgba(248,113,113,0.10)', bd:'rgba(248,113,113,0.5)', fg:'var(--red)' },
};

function StatusBox({ icon, label, state, text }) {
  return (
    <div style={{ flex:1, minWidth:0, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px' }}>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:4 }}>{icon} {label}</div>
      <div style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ width:7, height:7, borderRadius:'50%', background:DOT[state], display:'inline-block', flexShrink:0 }} />
        <span style={{ color: state==='ok' ? 'var(--text)' : DOT[state] }}>{text}</span>
      </div>
    </div>
  );
}

function RegionCard({ r, onView, isITLead }) {
  const headColor = r.critical > 0 ? 'var(--red)' : r.open > 0 ? 'var(--amber)' : 'var(--green)';
  return (
    <div className="card" style={{ borderLeft:`3px solid ${headColor}`, padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:600 }}>🏫 {r.region}</div>
          <div style={{ fontSize:12, color:'var(--text2)' }}>{r.campus_count} campus{r.campus_count!==1?'es':''}</div>
        </div>
        <button onClick={() => onView(r.region)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--accent)' }}>View all ↗</button>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        {isITLead && <StatusBox icon="🌐" label="Internet" state={r.internet.state} text={r.internet.label} />}
        <StatusBox icon="🖥" label="Server"   state={r.server.state}   text={r.server.label} />
        <StatusBox icon="💾" label="Storage"  state={r.storage.state}  text={r.storage.label} />
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
        {r.campuses.map(c => {
          const s = CHIP[c.status] || CHIP.ok;
          return <span key={c.id} style={{ fontSize:12, padding:'3px 10px', borderRadius:14, background:s.bg, border:`1px solid ${s.bd}`, color:s.fg }}>{c.name}</span>;
        })}
      </div>

      <div style={{ display:'flex', gap:28 }}>
        <div><div style={{ fontSize:20, fontWeight:700 }}>{r.open}</div><div style={{ fontSize:11, color:'var(--text2)' }}>Open</div></div>
        <div><div style={{ fontSize:20, fontWeight:700, color: r.critical>0?'var(--red)':'var(--text)' }}>{r.critical}</div><div style={{ fontSize:11, color:'var(--text2)' }}>Critical</div></div>
      </div>
    </div>
  );
}

// ── Region drill-down (matches the per-campus mockup) ──────────────────────
function RegionDetail({ region, onBack, isITLead }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/hq/region/${encodeURIComponent(region)}`).then(r => setD(r.data)).catch(()=>{}); }, [region]);
  if (!d) return <div style={{ color:'var(--text2)' }}>Loading {region}…</div>;
  const s = d.summary;
  const Stat = ({ label, value, color }) => (
    <div className="card" style={{ flex:1, minWidth:160 }}>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color:color||'var(--text)' }}>{value}</div>
    </div>
  );
  const Badge = ({ b }) => {
    const c = b.kind==='red'?CHIP.critical:b.kind==='amber'?CHIP.attention:CHIP.ok;
    return <span style={{ fontSize:12, padding:'3px 11px', borderRadius:14, background:c.bg, border:`1px solid ${c.bd}`, color:c.fg }}>{b.text}</span>;
  };
  const Box = ({ icon, title, children }) => (
    <div style={{ flex:1, minWidth:0, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 12px' }}>
      <div style={{ fontSize:11, color:'var(--text2)', marginBottom:6 }}>{icon} {title}</div>{children}
    </div>
  );
  const line = (text, state) => (
    <div style={{ fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
      <span style={{ width:7, height:7, borderRadius:'50%', background:DOT[state], flexShrink:0 }} />
      <span style={{ color: state==='ok' ? 'var(--text)' : DOT[state] }}>{text}</span>
    </div>
  );

  return (
    <div>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'var(--accent)', marginBottom:8 }}>◀ All regions</button>
      <h1 style={{ fontSize:21, fontWeight:700 }}>🏫 {region} region</h1>
      <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>{s.campuses} campuses · Last updated {new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} today</div>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:18 }}>
        <Stat label="Open tickets" value={s.open} color="var(--amber)" />
        <Stat label="Critical" value={s.critical} color="var(--red)" />
        {isITLead && <Stat label="ISP issues" value={`${s.isp_campuses} campus${s.isp_campuses!==1?'es':''}`} color="var(--orange)" />}
        <Stat label="All services OK" value={`${s.all_ok} campuses`} color="var(--green)" />
      </div>

      {d.campuses.map(c => {
        const hc = c.status==='critical'?'var(--red)':c.status==='attention'?'var(--amber)':'var(--border)';
        return (
          <div key={c.id} className="card" style={{ borderLeft:`3px solid ${hc}`, padding:16, marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap', marginBottom:12 }}>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <span style={{ width:38, height:38, borderRadius:8, background:'var(--bg3)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, color:'var(--text2)' }}>{c.abbr}</span>
                <div>
                  <div style={{ fontSize:15, fontWeight:600 }}>{c.name}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{c.updated_by ? `Updated by ${c.updated_by}${c.updated_at?' · '+c.updated_at+' today':''}` : 'Not yet checked today'}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>{c.badges.map((b,i) => <Badge key={i} b={b} />)}</div>
            </div>
            <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap' }}>
              {isITLead && <Box icon="🌐" title="Internet">
                {c.internet.lines.map((l,i) => line(`${l.name} — ${l.state==='down'?'Down':'OK'}`, l.state))}
                {c.internet.note && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>{c.internet.note}</div>}
              </Box>}
              <Box icon="🖥" title="Server">
                {line(c.server.text, c.server.state)}
                {c.server.note && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>{c.server.note}</div>}
              </Box>
              <Box icon="💾" title="Storage">
                {line(c.storage.text, c.storage.state)}
                {c.storage.note && <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>{c.storage.note}</div>}
              </Box>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:13 }}>
                <b style={{ color:c.open>0?'var(--amber)':'var(--text)' }}>{c.open}</b> open tickets&nbsp;&nbsp;
                <b style={{ color:c.critical>0?'var(--red)':'var(--text)' }}>{c.critical}</b> critical
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// How the service is landing with the people who use it: what staff said about
// closed tickets, and whether the AI assistant is helping. Both were already
// collected and shown only on screens nobody opens daily.
//
// It reads /api/dashboard, the same endpoint the Command Center uses, rather
// than growing a second query that could report different numbers for the
// same question.
function FeedbackStrip({ go }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/dashboard').then(r => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;

  const fb = d.staff_feedback || {};
  const ai = d.ai_feedback || {};
  if (!fb.total && !ai.asked_30) return null;

  const tone = (v, good, ok) => v == null ? 'var(--text3)'
    : v >= good ? 'var(--green)' : v >= ok ? 'var(--amber)' : 'var(--red)';

  const Cell = ({ label, value, colour, note, onClick }) => (
    <button type="button" onClick={onClick} disabled={!onClick}
      style={{ font:'inherit', textAlign:'left', background:'none', border:'none',
               padding:0, cursor: onClick ? 'pointer' : 'default', minWidth:120 }}>
      <div style={{ fontSize:11.5, color:'var(--text2)' }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color: colour || 'var(--text)',
                    fontVariantNumeric:'tabular-nums' }}>{value}</div>
      {note && <div style={{ fontSize:11.5, color:'var(--text2)' }}>{note}</div>}
    </button>
  );

  return (
    <div className="card" style={{ marginTop:18, display:'flex', gap:28, flexWrap:'wrap',
                                   alignItems:'flex-start' }}>
      <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:'.05em', color:'var(--text2)', width:'100%' }}>
        Feedback
      </div>

      {!!fb.total && (
        <>
          <Cell label="Staff rating (90 days)" value={fb.avg_rating}
            colour={tone(fb.avg_rating, 4, 3)}
            note={`${fb.total} rating${fb.total === 1 ? '' : 's'}`}
            onClick={go ? () => go('feedback') : null} />
          <Cell label="Unhappy (1–2★)" value={fb.unhappy}
            colour={fb.unhappy ? 'var(--red)' : 'var(--text2)'}
            note="last 90 days" onClick={go ? () => go('feedback') : null} />
        </>
      )}

      {!!ai.asked_30 && (
        <>
          <Cell label="AI questions (30 days)" value={ai.asked_30}
            note={`${ai.people} ${ai.people === 1 ? 'person' : 'people'}`}
            onClick={go ? () => go('ailogs') : null} />
          <Cell label="Rated helpful"
            value={ai.helpful_pct == null ? 'not rated' : `${ai.helpful_pct}%`}
            colour={tone(ai.helpful_pct, 70, 40)}
            note={ai.helpful_pct == null ? 'nobody has rated an answer'
                                         : `${ai.helpful} of ${ai.helpful + ai.not_helpful} rated`}
            onClick={go ? () => go('ailogs') : null} />
        </>
      )}
    </div>
  );
}

export function HQContent({ go }) {
  const { user } = useAuth();
  const isITLead = ['admin','it_head'].includes(user?.role);   // internet/ISP visible to IT Admin & IT Head only
  const [d, setD] = useState(null);
  const [region, setRegion] = useState(null);
  useEffect(() => { api.get('/hq/dashboard').then(r => setD(r.data)).catch(()=>{}); }, []);
  if (region) return <RegionDetail region={region} onBack={() => setRegion(null)} isITLead={isITLead} />;
  if (!d) return <div style={{ color:'var(--text2)' }}>Loading HQ view…</div>;
  const s = d.summary;
  const now = new Date().toLocaleString(undefined, { weekday:'short', day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  const FILTER_MAP = { new:{status:'Pending'}, pending:{open:'true'}, closed:{status:'Closed'}, critical:{priority:'High', open:'true'} };
  const openTickets = key => {   // click-through to the ticket list, pre-filtered
    if (!go) return;
    try { sessionStorage.setItem('hqTaskFilter', JSON.stringify(FILTER_MAP[key] || {})); } catch (e) {}
    go('tasks');
  };

  const Stat = ({ icon, label, value, sub, color, onClick }) => (
    <div className="card" onClick={onClick}
      style={{ flex:1, minWidth:170, cursor:onClick?'pointer':'default', transition:'transform .08s', position:'relative' }}
      onMouseEnter={e => onClick && (e.currentTarget.style.transform='translateY(-2px)')}
      onMouseLeave={e => e.currentTarget.style.transform='none'}>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>{icon} {label}</div>
      <div style={{ fontSize:26, fontWeight:700, color:color||'var(--text)' }}>{value}</div>
      <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>{onClick ? sub+' ›' : sub}</div>
    </div>
  );
  const Badge = ({ text, kind }) => {
    const c = kind==='red'?CHIP.critical:kind==='amber'?CHIP.attention:CHIP.ok;
    return <span style={{ fontSize:12, padding:'4px 12px', borderRadius:14, background:c.bg, border:`1px solid ${c.bd}`, color:c.fg }}>{text}</span>;
  };

  return (
    <div>
      {/* Campus IT and IT leads land here, so the welcome belongs on this page
          too. It shows once per sign-in and only for IT. */}
      <WelcomeScene go={go} />

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:18 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700 }}>📊 IT Operations — HQ View</h1>
          <div style={{ fontSize:13, color:'var(--text2)', marginTop:2 }}>TIPS Globeducate · {s.regions} regions · {s.campuses} campuses · {now}</div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <Badge kind="red"   text={`⛔ ${s.critical_total} critical`} />
          {isITLead && <Badge kind="amber" text={`⚠ ${s.isp_campuses} ISP issues`} />}
          {isITLead && <Badge kind={s.ups_campuses > 0 ? 'red' : 'ok'} text={s.ups_campuses > 0 ? `🔋 ${s.ups_campuses} UPS issue${s.ups_campuses>1?'s':''}` : '🔋 UPS OK'} />}
          <Badge kind="ok"    text={`✓ ${s.healthy} campuses healthy`} />
        </div>
      </div>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <Stat icon="🆕" label="New tickets"     value={s.new_total}      sub="awaiting assignment" color="var(--accent)" onClick={() => openTickets('new')} />
        <Stat icon="⏳" label="Pending"          value={s.pending_total}  sub="being worked on"     color="var(--amber)"  onClick={() => openTickets('pending')} />
        <Stat icon="✅" label="Closed"           value={s.closed_total}   sub="completed & closed"  color="var(--green)"  onClick={() => openTickets('closed')} />
        <Stat icon="⛔" label="Critical"         value={s.critical_total} sub="needs immediate action" color="var(--red)" onClick={() => openTickets('critical')} />
        {isITLead && <Stat icon="🌐" label="ISP issues" value={s.isp_campuses} sub="campuses affected" color="var(--orange)" />}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(420px, 1fr))', gap:14 }}>
        {d.regions.map(r => <RegionCard key={r.region} r={r} onView={setRegion} isITLead={isITLead} />)}
      </div>

      <FeedbackStrip go={go} />

      <div style={{ display:'flex', gap:18, flexWrap:'wrap', marginTop:18, fontSize:12, color:'var(--text2)' }}>
        <span>● <span style={{ color:'var(--green)' }}>All OK</span></span>
        <span>● <span style={{ color:'var(--amber)' }}>Needs attention</span></span>
        <span>● <span style={{ color:'var(--red)' }}>Issue / down</span></span>
        <span>▢ Campus OK</span><span>▢ Attention</span><span>▢ Critical</span>
      </div>
    </div>
  );
}

// Standalone page (header + sign out) — used for the Management role
export default function HQDashboard() {
  const { user, logout } = useAuth();
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 24px', borderBottom:'1px solid var(--border)', background:'var(--bg2)' }}>
        <div style={{ fontWeight:600, fontSize:15, color:'var(--accent)' }}>Management Console</div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <span style={{ fontSize:13, color:'var(--text2)' }}>{user.full_name}</span>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Sign out</button>
        </div>
      </div>
      <div style={{ padding:24, maxWidth:1200, margin:'0 auto' }}><HQContent /></div>
    </div>
  );
}
